// M004-F0747 (docs/design/M004-F0744-F0747-Envelope-Workspace.md sections 3.3, 8, 8.0) - the claimant's side of an expense claim, as
// pure functions: the sentences for the dry-run issues, the organisation picker, whether "Submit claim" is offered and why not, the
// claim banner, and the "needs your attention" count. T-M004-ENVUX-011 / -020 prove the screens; claimModel.test.js proves these.
// The claimant NEVER gets approve / return controls from this file or its callers (a claimant cannot approve their own claim).
import { formatMoney } from './envelopeWorkspaceModel';

export const NOT_SAME_LEDGER = 'Not on the same Ledger yet';
const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

// The Ledger's dry-run issue codes (Spec 1.4.0 EnvelopeClaimIssue): one blocking sentence each.
export const issueSentence = (issue) => {
  const n = Array.isArray(issue?.receipt_document_ids) ? issue.receipt_document_ids.length : 0;
  switch (issue?.code) {
    case 'empty': return 'This envelope has no receipts.';
    case 'missing_amount': return `${plural(n, 'receipt has', 'receipts have')} no amount`;
    case 'unconfirmed': return `${plural(n, 'receipt still needs', 'receipts still need')} the suggested amount confirmed`;
    case 'mixed_currency': return 'The receipts are in more than one currency; a claim uses one currency';
    default: return `This envelope cannot be submitted yet (${issue?.code || 'unknown'})`;
  }
};

// The receipts "Show them" filters the grid to ('' when the issue names none).
export const issueIds = (issue) => (Array.isArray(issue?.receipt_document_ids) ? issue.receipt_document_ids.slice() : []);

export const summaryLine = (summary) => {
  const count = Number(summary?.receipt_count) || 0;
  const money = summary?.currency ? formatMoney(summary.total_minor, summary.currency) : '';
  const vat = Number(summary?.vat_minor) > 0 ? ` (VAT ${formatMoney(summary.vat_minor)})` : '';
  return `${plural(count, 'receipt', 'receipts')}${money ? ` - ${money}${vat}` : ''}`;
};

// Claim-to options: every organisation I belong to with a Ledger, the envelope's own preselected. An organisation known to be on ANOTHER
// Ledger installation is listed disabled ("Not on the same Ledger yet", design 8.3): the move is same-installation only.
const installationOf = (org) => org?.ledger_installation_uuid || org?.ledgerInstallationUuid || '';
export const claimOrgOptions = (organisations, ownOrgId) => {
  const list = Array.isArray(organisations) ? organisations : [];
  const own = list.find((org) => String(org.id) === String(ownOrgId));
  const ownInstallation = installationOf(own);
  return list.map((org) => {
    const theirs = installationOf(org);
    const other = Boolean(ownInstallation && theirs && ownInstallation !== theirs);
    return { id: String(org.id), label: org.name || org.organisation_name || `Organisation ${org.id}`, disabled: other, reason: other ? NOT_SAME_LEDGER : '', own: String(org.id) === String(ownOrgId) };
  });
};

// '' when "Submit claim" works; otherwise why not (the button is disabled WITH this reason, never silently).
export const submitClaimReason = (envelope, { offline = false, receiptCount = 0 } = {}) => {
  if (offline) return 'Ledger is not reachable - changes are off until it is back.';
  if (!envelope) return 'Choose an envelope first.';
  if (envelope.pending) return 'Creating the envelope - you can submit it in a moment.';
  if (envelope.locked) return 'Submitted - it unlocks if the reviewer returns it.';
  if (envelope.status === 'closed') return 'Closed - reopen it to submit a claim.';
  if (receiptCount === 0) return 'The envelope is empty - add receipts first.';
  return '';
};

// Offered at all? Not on Unsorted (the inbox), not on a deleted envelope, not while the claim is in review / approved / paid / rejected.
export const offersSubmit = (envelope) => Boolean(envelope) && envelope.kind !== 'unsorted' && envelope.status !== 'deleted'
  && !(envelope.claim && ['submitted', 'approved', 'reimbursed', 'rejected'].includes(envelope.claim.state));

export const isResubmit = (envelope) => envelope?.claim?.state === 'returned';

// A returned (or rejected) claim the claimant has not acknowledged yet.
export const needsAttention = (record) => record?.claim?.attention === 'returned' || record?.claim?.attention === 'rejected';
export const attentionCount = (records) => (Array.isArray(records) ? records : []).filter(needsAttention).length;

const STATE_TEXT = {
  submitted: 'Submitted - waiting for a reviewer. The envelope is locked.',
  approved: 'Approved. The envelope stays locked.',
  reimbursed: 'Approved and reimbursed. The envelope stays locked.',
  returned: 'Returned by the reviewer. The envelope is unlocked - edit it and resubmit.',
  rejected: 'Rejected by the reviewer. This claim is closed.',
};

// The banner above the grid. `detail` is the Ledger's claim view (envelope.claim.get) - it carries the reviewer's `reason`.
export const claimBanner = (envelope, detail) => {
  const claim = envelope?.claim;
  if (!claim || !STATE_TEXT[claim.state]) return null;
  const returnedOrRejected = claim.state === 'returned' || claim.state === 'rejected';
  const reason = returnedOrRejected ? String(detail?.reason || '').trim() : '';
  return {
    state: claim.state,
    tone: returnedOrRejected ? 'warning' : 'locked',
    title: { submitted: 'Claim submitted', approved: 'Claim approved', reimbursed: 'Claim reimbursed', returned: 'Claim returned', rejected: 'Claim rejected' }[claim.state],
    text: STATE_TEXT[claim.state],
    transactionId: claim.transaction_id || '',
    orgId: claim.org_id,
    reason,
    needsReason: returnedOrRejected,
    attention: needsAttention(envelope),
    canResubmit: claim.state === 'returned',
  };
};

// The record the Ledger's claim view implies (submit / ack answers), merged into the list entry.
export const claimPatch = (view) => (view && view.transaction_id ? {
  claim: { state: view.state, transaction_id: view.transaction_id, org_id: view.org_id, attention: view.attention ?? null },
  locked: ['submitted', 'approved', 'reimbursed'].includes(view.state),
} : null);
